import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, ExternalLink, Calendar, Search } from "lucide-react";
import { api } from "../api";
import type { CheckIn, WeekOption } from "../types";
import { WeightTrendChart } from "./WeightTrendChart";

/** Earliest week users can navigate to — fresh-start date */
const FRESH_START_DATE = "2026-06-15";

function getMonday(d: Date): string {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  return date.toISOString().split("T")[0];
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().split("T")[0];
}

/** Normalize any date string to YYYY-MM-DD for <input type="date"> */
function normalizeDate(v: string | null | undefined): string {
  if (!v) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  return isNaN(d.getTime()) ? "" : d.toISOString().split("T")[0];
}

/** Format ISO date string to DD/MM/YYYY for display */
function fmtDDMMYYYY(d: string | null): string {
  if (!d) return "";
  const parts = d.split("-");
  if (parts.length !== 3) return d;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

export function CheckIns() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const saved = searchParams.get("week");
    if (saved) return saved;
    return getMonday(new Date());
  });
  const [checkins, setCheckins] = useState<CheckIn[]>([]);
  const [weekLabel, setWeekLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(searchParams.get("filter") || "all");
  const [weeks, setWeeks] = useState<WeekOption[]>([]);
  const [showWeekPicker, setShowWeekPicker] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [allMembers, setAllMembers] = useState<any[]>([]);

  const loadWeek = useCallback(async (weekStart: string) => {
    setLoading(true);
    try {
      const res = await api.getWeekCheckIns(weekStart);
      setCheckins(res.checkins);
      setWeekLabel(res.week_label);
      if (res.members) setAllMembers(res.members);
    } catch (e) {
      console.error(e);
      setCheckins([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadWeek(currentWeekStart); }, [currentWeekStart, loadWeek]);

  useEffect(() => {
    api.getWeeks().then((r) => setWeeks(r.weeks)).catch(console.error);
  }, []);

  const goToPrevWeek = () => {
    const prev = addDays(currentWeekStart, -7);
    if (prev < FRESH_START_DATE) return;
    setCurrentWeekStart(prev);
    setSearchParams({ week: prev });
  };

  const goToNextWeek = () => {
    const next = addDays(currentWeekStart, 7);
    setCurrentWeekStart(next);
    setSearchParams({ week: next });
  };

  const goToThisWeek = () => {
    const monday = getMonday(new Date());
    setCurrentWeekStart(monday);
    setSearchParams({ week: monday });
  };

  const canGoPrev = addDays(currentWeekStart, -7) >= FRESH_START_DATE;

  const handleUpdateCheckin = async (id: string, field: string, value: any) => {
    setSavingId(id);
    try {
      const res = await api.updateCheckIn(id, { [field]: value });
      setCheckins((prev) => prev.map((c) => c.id === id ? { ...c, ...res.checkin } : c));
    } catch (e) {
      console.error(e);
    } finally {
      setSavingId(null);
    }
  };

  const handleBulkUpdate = async (id: string, updates: Record<string, any>) => {
    setSavingId(id);
    try {
      const res = await api.updateCheckIn(id, updates);
      setCheckins((prev) => prev.map((c) => c.id === id ? { ...c, ...res.checkin } : c));
    } catch (e) {
      console.error(e);
    } finally {
      setSavingId(null);
    }
  };

  // Members who don't have a check-in yet this week
  const checkedInMemberIds = new Set(checkins.map((c) => c.member_id));
  const missingMembers = allMembers.filter((m) => !checkedInMemberIds.has(m.id));

  // Only show missing members on All and Not Submitted tabs
  const showMissingMembers = filter === "all" || filter === "not_submitted";

  const handleCreateCheckin = async (memberId: string) => {
    try {
      const res = await api.createCheckIn({
        member_id: memberId,
        week_start_date: currentWeekStart,
      });
      // Refresh the week to pick up the new row
      loadWeek(currentWeekStart);
    } catch (e) {
      console.error(e);
    }
  };

  const filtered = checkins.filter((c) => {
    // Tab filter
    if (filter === "submitted" && !c.member_submitted) return false;
    if (filter === "not_submitted" && c.member_submitted) return false;
    if (filter === "needs_review" && !(c.member_submitted && !c.coach_reviewed)) return false;
    // Search filter (applies on top of any tab)
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (c.member_name || "").toLowerCase().includes(q);
    }
    return true;
  });

  // Not Submitted count includes members without any check-in row
  const notSubmittedCount = missingMembers.length + checkins.filter((c) => !c.member_submitted).length;
  const submittedCount = checkins.filter((c) => c.member_submitted).length;
  const needsReviewCount = checkins.filter((c) => c.member_submitted && !c.coach_reviewed).length;
  // Use total active members for the denominator so it makes sense across all weeks
  const totalCount = allMembers.length || checkins.length;

  return (
    <div className="space-y-6">
      {/* Header with week navigation */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Check-Ins</h1>
      </div>

      {/* Week navigation bar */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <button onClick={goToPrevWeek} disabled={!canGoPrev} className={`p-2 rounded-lg transition-colors ${canGoPrev ? "hover:bg-gray-100 text-gray-600" : "text-gray-300 cursor-not-allowed"}`}><ChevronLeft className="w-5 h-5" /></button>
          <div className="text-center flex-1">
            <p className="text-sm font-semibold text-gray-900">{weekLabel || currentWeekStart}</p>
            <p className="text-xs text-gray-500 mt-0.5">{submittedCount} / {totalCount} submitted</p>
          </div>
          <button onClick={goToNextWeek} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><ChevronRight className="w-5 h-5 text-gray-600" /></button>
        </div>
        <div className="flex items-center justify-center gap-3 mt-3">
          <button onClick={goToThisWeek} className="text-xs px-3 py-1.5 bg-[#e2c4b6]/30 text-[#5f1b2c] rounded-full font-medium hover:bg-[#e2c4b6]/50 transition-colors">This Week</button>
          <div className="relative">
            <button onClick={() => setShowWeekPicker(!showWeekPicker)} className="text-xs px-3 py-1.5 bg-gray-100 text-gray-600 rounded-full font-medium hover:bg-gray-200 transition-colors">Pick a Week</button>
            {showWeekPicker && (
              <div className="absolute top-full mt-1 right-0 bg-white border border-gray-200 rounded-xl shadow-lg z-50 w-72 max-h-64 overflow-y-auto">
                {weeks.filter((w) => w.start >= FRESH_START_DATE).map((w) => (
                  <button key={w.start} onClick={() => {
                    setCurrentWeekStart(w.start);
                    setSearchParams({ week: w.start });
                    setShowWeekPicker(false);
                  }} className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${w.start === currentWeekStart ? "bg-[#e2c4b6]/20 font-medium text-[#5f1b2c]" : "text-gray-700"}`}>{w.label}</button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {([
          { key: "all", label: "All" },
          { key: "submitted", label: "Submitted", count: submittedCount },
          { key: "not_submitted", label: "Not Submitted", count: notSubmittedCount },
          { key: "needs_review", label: "Needs Review", count: needsReviewCount },
        ] as const).map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${filter === f.key ? "bg-[#5f1b2c] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{f.label}{"count" in f && f.count > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${filter === f.key ? "bg-white/20 text-white" : "bg-[#5f1b2c] text-white"}`}>{f.count}</span>}</button>
        ))}
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search by member name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#5f1b2c] focus:ring-1 focus:ring-[#5f1b2c]/30 text-sm shadow-sm"
        />
      </div>

      {/* Check-in rows or empty state */}
      {loading ? (
        <div className="space-y-2">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-20 bg-gray-50 rounded-xl animate-pulse" />)}</div>
      ) : filtered.length === 0 && (!showMissingMembers || missingMembers.length === 0) ? (
        <div className="text-center py-12 text-gray-400">
          <p className="text-lg">
            {filter === "submitted" ? "No submitted check-ins for this week"
              : filter === "needs_review" ? "No check-ins needing review this week"
              : "No check-ins for this week"}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Existing check-in rows */}
          {filtered.map((c) => (
            <CheckinRow key={c.id} checkin={c} onSave={handleBulkUpdate} saving={savingId === c.id} />
          ))}
          {/* Members without a check-in yet — only on All and Not Submitted tabs */}
          {showMissingMembers && missingMembers.length > 0 && (
            <div className="bg-white border border-dashed border-gray-300 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-3 font-medium">Add check-in for a member</p>
              <div className="flex flex-wrap gap-2">
                {missingMembers.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => handleCreateCheckin(m.id)}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-[#e2c4b6]/30 border border-gray-200 rounded-lg text-sm text-gray-700 hover:text-[#5f1b2c] transition-colors"
                  >
                    <span className="w-6 h-6 rounded-full bg-[#e2c4b6]/40 flex items-center justify-center text-[#5f1b2c] font-bold text-[10px] shrink-0">
                      {m.name?.split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase()}
                    </span>
                    {m.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Local-state driven check-in row.
 * Edits live in local state; saves happen on blur (text/date) or immediately (checkboxes).
 */
function CheckinRow({ checkin: c, onSave, saving }: { checkin: CheckIn; onSave: (id: string, updates: Record<string, any>) => Promise<void>; saving: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const [historyCheckins, setHistoryCheckins] = useState<CheckIn[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Local editable state — initialised from the server checkin
  const [localDate, setLocalDate] = useState(() => normalizeDate(c.check_in_date));
  const [localWeight, setLocalWeight] = useState(c.current_weight != null ? String(c.current_weight) : "");
  const [localDrive, setLocalDrive] = useState(c.google_drive_link || "");
  const [localNotes, setLocalNotes] = useState(c.client_checkin_notes || "");
  const [localPhoto, setLocalPhoto] = useState(c.photo_submitted);
  const [localSubmitted, setLocalSubmitted] = useState(c.member_submitted);
  const [localReviewed, setLocalReviewed] = useState(c.coach_reviewed);
  const [localMethod, setLocalMethod] = useState(c.checkin_method_this_week || "Voice message");

  // Track what has changed so we only save dirty fields on blur
  const dirtyRef = useRef<Record<string, any>>({});

  // Fetch member's full check-in history when expanded
  useEffect(() => {
    if (!expanded || !c.member_id) return;
    setHistoryLoading(true);
    api.getMemberCheckIns(c.member_id)
      .then((r) => setHistoryCheckins(r.checkins))
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, [expanded, c.member_id]);

  // Keep history in sync when parent pushes updated checkin (after save)
  useEffect(() => {
    if (historyCheckins.length === 0) return;
    setHistoryCheckins((prev) =>
      prev.map((hc) => (hc.id === c.id ? { ...hc, ...c } : hc))
    );
  }, [c.id, c.current_weight, c.member_submitted, c.coach_reviewed, c.check_in_date]);

  // Re-sync when the parent pushes a fresh checkin (e.g. after save completes)
  useEffect(() => {
    setLocalDate(normalizeDate(c.check_in_date));
    setLocalWeight(c.current_weight != null ? String(c.current_weight) : "");
    setLocalDrive(c.google_drive_link || "");
    setLocalNotes(c.client_checkin_notes || "");
    setLocalPhoto(c.photo_submitted);
    setLocalSubmitted(c.member_submitted);
    setLocalReviewed(c.coach_reviewed);
    setLocalMethod(c.checkin_method_this_week || "Voice message");
  }, [c.id, c.check_in_date, c.current_weight, c.google_drive_link, c.client_checkin_notes, c.photo_submitted, c.member_submitted, c.coach_reviewed, c.checkin_method_this_week]);

  /** Mark a field as dirty (pending save) */
  const markDirty = (field: string, value: any) => {
    dirtyRef.current[field] = value;
  };

  /** Flush all dirty fields to the server */
  const flushDirty = async () => {
    const payload = { ...dirtyRef.current };
    dirtyRef.current = {};
    if (Object.keys(payload).length > 0) {
      // Optimistically update the history chart immediately
      if ("current_weight" in payload) {
        const newWeight = payload.current_weight;
        setHistoryCheckins((prev) => {
          const updated = prev.map((hc) =>
            hc.id === c.id ? { ...hc, current_weight: newWeight } : hc
          );
          // If this checkin wasn't in history yet, add it
          if (!prev.some((hc) => hc.id === c.id)) {
            updated.push({ ...c, current_weight: newWeight });
          }
          return updated;
        });
      }
      await onSave(c.id, payload);
    }
  };

  /** Save a single checkbox immediately (optimistic) */
  const saveCheckbox = async (field: string, value: boolean) => {
    await onSave(c.id, { [field]: value });
  };

  return (
    <div className={`bg-white border rounded-xl overflow-hidden transition-all ${c.membership_type === "VIP" ? "border-pink-200 bg-pink-50/30" : localSubmitted ? "border-emerald-200" : "border-gray-200"}`}>
      {/* Main row - clickable to expand */}
      <button onClick={() => setExpanded(!expanded)} className="w-full text-left p-4 hover:bg-gray-50/50 transition-colors">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#e2c4b6]/40 flex items-center justify-center text-[#5f1b2c] font-bold text-xs shrink-0">
            {c.member_name?.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase() || "?"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 text-sm truncate">{c.member_name || "Unknown"}</span>
              {c.membership_type && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${c.membership_type === "VIP" ? "bg-pink-100 text-pink-700" : "bg-blue-50 text-blue-700"}`}>{c.membership_type}</span>}
            </div>
            <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
              {c.checkin_method_last_week && <span>Last: {c.checkin_method_last_week}</span>}
              {localMethod && <span className="font-medium text-[#5f1b2c]">This week: {localMethod}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${localSubmitted ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>{localSubmitted ? "Submitted" : "Missing"}</span>
            {localReviewed && <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-blue-50 text-blue-700">Reviewed</span>}
          </div>
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50/50 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {/* Date picker with DD/MM/YYYY hint */}
            <div>
              <label className="text-xs text-gray-500 block mb-1">Check-in Date</label>
              <div className="relative">
                <input
                  type="date"
                  value={localDate}
                  onChange={async (e) => {
                    const v = e.target.value;
                    setLocalDate(v);
                    await onSave(c.id, { check_in_date: v || null });
                  }}
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-[#5f1b2c]"
                  disabled={saving}
                />
                {localDate && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none flex items-center gap-1">
                    <Calendar className="w-3 h-3" />{fmtDDMMYYYY(localDate)}
                  </span>
                )}
              </div>
            </div>

            {/* Weight — text input that accepts paste and decimals */}
            <div>
              <label className="text-xs text-gray-500 block mb-1">Current Weight</label>
              <input
                type="text"
                inputMode="decimal"
                value={localWeight}
                onChange={(e) => {
                  setLocalWeight(e.target.value);
                  markDirty("current_weight", e.target.value ? parseFloat(e.target.value) : null);
                }}
                onBlur={flushDirty}
                onKeyDown={(e) => { if (e.key === "Enter") flushDirty(); }}
                placeholder="kg"
                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-[#5f1b2c]"
                disabled={saving}
              />
            </div>

            {/* Google Drive link */}
            <div>
              <label className="text-xs text-gray-500 block mb-1">Google Drive Link</label>
              <div className="flex gap-1">
                <input
                  type="text"
                  value={localDrive}
                  onChange={(e) => {
                    setLocalDrive(e.target.value);
                    markDirty("google_drive_link", e.target.value || null);
                  }}
                  onBlur={flushDirty}
                  onKeyDown={(e) => { if (e.key === "Enter") flushDirty(); }}
                  placeholder="https://..."
                  className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-[#5f1b2c]"
                  disabled={saving}
                />
                {localDrive && (
                  <a href={localDrive} target="_blank" rel="noopener noreferrer" className="p-2 text-[#5f1b2c] hover:bg-white rounded-lg border border-gray-200 transition-colors"><ExternalLink className="w-4 h-4" /></a>
                )}
              </div>
              {localDrive && (
                <div className="mt-2">
                  <DriveLinkPreview url={localDrive} />
                </div>
              )}
            </div>
          </div>

          {/* Notes — bigger box */}
          <div className="mt-4">
            <label className="text-xs text-gray-500 block mb-1">Check-In Notes</label>
            <textarea
              value={localNotes}
              onChange={(e) => {
                setLocalNotes(e.target.value);
                markDirty("client_checkin_notes", e.target.value || null);
              }}
              onBlur={flushDirty}
              rows={6}
              placeholder="Notes from this week's check-in..."
              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-[#5f1b2c] resize-y min-h-[120px]"
              disabled={saving}
            />
          </div>

          {/* Check-in method selector */}
          <div className="flex items-center gap-6 mt-4 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500">This week's method:</label>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden bg-white">
                {["Voice message", "DM"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={async () => {
                      setLocalMethod(m);
                      await onSave(c.id, { checkin_method_this_week: m });
                    }}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${localMethod === m ? "bg-[#5f1b2c] text-white" : "text-gray-600 hover:bg-gray-50"}`}
                  >{m}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Checkboxes — save immediately on click */}
          <div className="flex items-center gap-6 mt-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={localPhoto}
                onChange={async (e) => {
                  const v = e.target.checked;
                  setLocalPhoto(v);
                  await saveCheckbox("photo_submitted", v);
                }}
                className="w-4 h-4 rounded border-gray-300 text-[#5f1b2c] focus:ring-[#5f1b2c]"
                disabled={saving}
              />
              <span className="text-sm text-gray-700">Photo submitted</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={localSubmitted}
                onChange={async (e) => {
                  const v = e.target.checked;
                  setLocalSubmitted(v);
                  await saveCheckbox("member_submitted", v);
                }}
                className="w-4 h-4 rounded border-gray-300 text-[#5f1b2c] focus:ring-[#5f1b2c]"
                disabled={saving}
              />
              <span className="text-sm text-gray-700">Member submitted</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={localReviewed}
                onChange={async (e) => {
                  const v = e.target.checked;
                  setLocalReviewed(v);
                  await saveCheckbox("coach_reviewed", v);
                }}
                className="w-4 h-4 rounded border-gray-300 text-[#5f1b2c] focus:ring-[#5f1b2c]"
                disabled={saving}
              />
              <span className="text-sm text-gray-700">Coach reviewed</span>
            </label>
          </div>

          {/* Weight Trend Chart */}
          {historyLoading ? (
            <div className="mt-4 h-48 bg-white rounded-xl border border-gray-200 animate-pulse" />
          ) : historyCheckins.length > 0 ? (
            <div className="mt-4">
              <WeightTrendChart checkins={historyCheckins} />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function DriveLinkPreview({ url }: { url: string }) {
  const [failed, setFailed] = useState(false);

  // Try to extract a Google Drive file ID and build a thumbnail URL
  const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (!match) return null;
  const fileId = match[1];
  const thumbUrl = `https://drive.google.com/thumbnail?id=${fileId}&sz=w400`;

  if (failed) return null;

  return (
    <div className="rounded-lg overflow-hidden border border-gray-200 bg-white">
      <img src={thumbUrl} alt="Drive file preview" className="w-full max-h-48 object-contain bg-gray-50" onError={() => setFailed(true)} />
    </div>
  );
}